/**
 * Productos y servicios — lista, `TDS-CORE` Parte 2 §1.
 *
 * Estados de `OD-0017 D-03`: cargando, vacío (con la siguiente acción), con
 * datos, error y sin permiso. La lista llega entera (máximo 100 por plan) y se
 * filtra aquí: buscar no hace otra petición por cada tecla.
 */
import { useCallback, useEffect, useState } from "react";
import { api, type Producto } from "../api/cliente";
import { t } from "../i18n/es";
import { coincide } from "../ui/coincide";
import { precioLegible } from "../ui/formato";
import { Icono } from "../ui/Iconos";
import { Aviso, Boton, Campo, FilaPulsable, Tarjeta } from "../ui/primitivas";

type Estado =
  | { readonly tipo: "cargando" }
  | { readonly tipo: "error" }
  | { readonly tipo: "sin-permiso" }
  | { readonly tipo: "datos"; readonly productos: readonly Producto[] };

export function resumenIva(p: Pick<Producto, "vatTreatment" | "vatRate" | "vatCause">): string {
  if (p.vatTreatment === "SUJETA_EXENTA") return t.productos.exenta(p.vatCause ?? "");
  if (p.vatTreatment === "NO_SUJETA") return t.productos.noSujeta(p.vatCause ?? "");
  return t.productos.iva(p.vatRate ?? "");
}

export function PantallaProductos({
  onAbrir,
  onNuevo,
  onSesionCaducada,
}: {
  onAbrir: (id: string) => void;
  onNuevo: () => void;
  onSesionCaducada: () => void;
}) {
  const [activos, setActivos] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [estado, setEstado] = useState<Estado>({ tipo: "cargando" });

  const cargar = useCallback(async (): Promise<void> => {
    setEstado({ tipo: "cargando" });
    const r = await api.listarProductos();
    if (!r.ok) {
      if (r.estado === 401) onSesionCaducada();
      else setEstado({ tipo: r.estado === 403 ? "sin-permiso" : "error" });
      return;
    }
    setEstado({ tipo: "datos", productos: r.datos });
  }, [onSesionCaducada]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // Sin NINGÚN producto, la pantalla explica para qué sirve esto y enseña tres
  // ejemplos. Son una ILUSTRACIÓN: no se guardan, no se pueden pulsar y no
  // crean nada (OD-0015 D-04 prohíbe crear datos de ejemplo, no describirlos).
  if (estado.tipo === "datos" && estado.productos.length === 0) {
    return (
      <Tarjeta titulo={t.productos.titulo}>
        <div className="estado-vacio">
          <span className="estado-vacio-icono" aria-hidden="true">
            <Icono nombre="productos" />
          </span>
          <p className="estado-vacio-titulo">{t.productos.vacioTitulo}</p>
          <p>{t.productos.vacio}</p>
          <div className="ejemplos">
            <p className="ayuda-neutra">{t.productos.ejemplosTitulo}</p>
            <ul>
              {t.productos.ejemplos.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </div>
          <Boton onClick={onNuevo}>
            <Icono nombre="mas" />
            {t.productos.crearPrimero}
          </Boton>
        </div>
      </Tarjeta>
    );
  }

  const delFiltro = estado.tipo === "datos" ? estado.productos.filter((p) => p.active === activos) : [];
  // Sin tildes y por palabras: «reforma bano» encuentra «Reforma de baño».
  const visibles = delFiltro.filter((p) => coincide(busqueda, p.code, p.description));

  return (
    <Tarjeta titulo={t.productos.titulo}>
      <div className="barra-acciones">
        <div className="segmentado" role="group" aria-label={t.productos.filtro}>
          {[true, false].map((a) => (
            <button key={String(a)} type="button" className="menu-opcion" aria-pressed={activos === a} onClick={() => setActivos(a)}>
              {a ? t.productos.activos : t.productos.inactivos}
            </button>
          ))}
        </div>
        <Boton onClick={onNuevo} deshabilitado={estado.tipo !== "datos"}>
          {t.productos.nuevo}
        </Boton>
      </div>

      {estado.tipo === "datos" && estado.productos.length > 0 ? (
        <Campo id="prd-buscar" etiqueta={t.productos.buscar} valor={busqueda} onChange={setBusqueda} autoComplete="off" requerido={false} />
      ) : null}

      {estado.tipo === "cargando" ? <p>{t.productos.cargando}</p> : null}
      {estado.tipo === "sin-permiso" ? <Aviso tono="aviso">{t.productos.sinPermiso}</Aviso> : null}
      {estado.tipo === "error" ? (
        <>
          <Aviso tono="error">{t.productos.error}</Aviso>
          <Boton variante="secundario" onClick={() => void cargar()}>
            {t.productos.reintentar}
          </Boton>
        </>
      ) : null}

      {estado.tipo === "datos" && delFiltro.length === 0 ? (
        <Aviso tono="info">{activos ? t.productos.vacioActivos : t.productos.vacioInactivos}</Aviso>
      ) : null}
      {estado.tipo === "datos" && delFiltro.length > 0 && visibles.length === 0 ? (
        <Aviso tono="info">{t.productos.sinResultados}</Aviso>
      ) : null}

      {visibles.length > 0 ? (
        <ul className="lista-filas">
          {visibles.map((p) => (
            <FilaPulsable key={p.id} onClick={() => onAbrir(p.id)}>
              <span className="fila-titulo">
                {p.code} · {p.description}
              </span>
              <span className="ayuda-neutra">
                {t.productos.precioSinIva(precioLegible(p.unitPrice))} · {resumenIva(p)}
              </span>
            </FilaPulsable>
          ))}
        </ul>
      ) : null}
    </Tarjeta>
  );
}
